import { Platform } from '@ail/orders-api'
import React, { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, useLocation } from 'react-router-dom'
import { FrontendSettings } from '../../../helpers/FrontendSettings'
import { useAuth } from '../../../hooks/useAuth'
import { useContractSupport } from '../../../hooks/useContractSupport'
import { useGetOrdersFormsQuery } from '../../../redux/api/ordersApi'
import { FrontendSettingsKeys } from '../../../types/enums'
import { DriverModal } from '../../Driver/DriverModal'
import { PermissionManager } from './../../../helpers/PermissionManager'
import { isDevAndStaging, mobileVersion } from './../../../types/constants'
import { IVersionInfo } from './../../../types/interfaces'
import ver from './../../../version.json'
import ChangeLanguage from './ChangeLanguage/ChangeLanguage'
import './Header.scss'
import HelpComponentFunctional from './HelpComponent/HelpComponentFunctional'
import { ManagerSelector } from './ManagerSelector/ManagerSelector'
import UserMenuSettings from './UserMenuSettings/UserMenuSettings'
import { ContractSearchNew } from './contractSearch/ContractSearchFunctional'
import { TenantSelector } from './TenantSelector/TenantSelector'

export default function Header(props: { sidebarExpanded: (val: boolean) => void }) {
  const { user } = useAuth()
  const { t } = useTranslation('common')
  const location = useLocation()
  const { contractSupportRef, disableContractSupport } = useContractSupport()
  const [sidebarExpanded, setSidebarExpanded] = React.useState(false)
  const [sidebarClicked, setSidebarClicked] = React.useState(false)
  const [showUserSubmenu, setShowUserSubmenu] = React.useState(false)
  const [ShowEdgePop, setShowEdgePop] = React.useState(false)
  const [helpDisplay, setHelpDisplay] = React.useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const helpDisplayRef = useRef<HTMLDivElement>(null)
  const helpDisplayButtonRef = useRef<HTMLButtonElement>(null)
  const version = ver as IVersionInfo

  const orderForms = useGetOrdersFormsQuery()
  const hasOrderForms = useMemo(() => {
    return orderForms.currentData?.some((f) => f.platform === Platform.Interfleet4U)
  }, [orderForms.currentData])

  useEffect(() => {
    if (contractSupportRef) {
      setHelpDisplay(true)
    }
  }, [contractSupportRef])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (!userMenuRef?.current?.contains(event.target as Node) && window.location.pathname !== '/userPreferences') {
        setShowUserSubmenu(false)
      }
    }
    if (showUserSubmenu) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showUserSubmenu])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        !helpDisplayRef?.current?.contains(event.target as Node) &&
        !helpDisplayButtonRef?.current?.contains(event.target as Node)
      ) {
        setHelpDisplay(false)
        disableContractSupport()
      }
    }
    if (helpDisplay) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [disableContractSupport, helpDisplay])

  const expandSidebar = (val: boolean) => {
    setSidebarClicked(val)
    props.sidebarExpanded(val)
    setSidebarExpanded(val)
  }

  function handleSupportMenuClose() {
    setHelpDisplay(false)
    if (helpDisplay) {
      disableContractSupport()
    }
  }

  function activeMenuItem(urls: string[], exact?: boolean): string {
    if (urls.find((u) => (exact ? u === location.pathname : location.pathname.indexOf(u) === 0))) {
      return 'active'
    }
    return 'inactive'
  }

  return (
    <>
      <div className="header vertical">
        <div className="logoArea">
          <a href="/" title="Interfleet4U | Auto-Interleasing Fleet Management System">
            <img
              src="/interfleet4u-new.svg"
              className="logo"
              width="160"
              height="34"
              alt="Interfleet4U | Auto-Interleasing Fleet Management System"
            />
            {/* <img
              src="/interfleet4u-xmas.svg"
              className="logo"
              width="160"
              height="60"
              alt="Interfleet4U | Auto-Interleasing Fleet Management System"
            ></img> */}
          </a>
          {!mobileVersion && (
            <>
              <ContractSearchNew />
              {!!user?.isInternalUser && <ManagerSelector />}
              {!!user?.isInternalUser && <TenantSelector />}
            </>
          )}
        </div>
        <div className="rightArea">
          {user?.isInternalUser && <DriverModal />}
          {(window.navigator.userAgent.includes('Edg/') || window.navigator.userAgent.includes('Edge/')) && (
            <div className="edgeArea">
              <img
                src="/edge-problem.svg"
                className="edgeProblem"
                width="40"
                height="30"
                alt="Interfleet4U | Auto-Interleasing Fleet Management System"
                onClick={() => setShowEdgePop(!ShowEdgePop)}
              />
              <div className={`edge ${ShowEdgePop ? 'visible' : 'hidden'}`}>
                <i className="icon-warning" />
                <span>{t('PLEASE_USE_DIFFERENT_BROWSER')}</span>
              </div>
            </div>
          )}
          {!mobileVersion && (
            <div className={helpDisplay === true ? 'helpArea active' : 'helpArea'}>
              <button
                ref={helpDisplayButtonRef}
                onClick={() => {
                  setHelpDisplay(!helpDisplay)
                  if (helpDisplay) {
                    disableContractSupport()
                  }
                }}
              >
                <i className="icon-help" />
                <div className="triangle" />
              </button>
              <div ref={helpDisplayRef}>
                {helpDisplay ? <HelpComponentFunctional onClose={handleSupportMenuClose} /> : null}
              </div>
            </div>
          )}
          <div className="userMenuArea" ref={userMenuRef}>
            <i className="icon-settings" onClick={() => setShowUserSubmenu(!showUserSubmenu)} />
            <div className={`userMenu ${showUserSubmenu ? 'visible' : 'hidden'}`}>
              <div className="userInfo">
                <i className="icon-user" />
                {!!user && (
                  <div>
                    <div className="userRole">{user.displayName}</div>
                    <span className="userMail">{user.email}</span>
                  </div>
                )}
              </div>
              {!mobileVersion && (
                <NavLink to="/userPreferences" className="userInfo">
                  <i className="icon-user-pref" />
                  {t('PREFERENCES')}
                </NavLink>
              )}
              <NavLink to="/releaseNotes" className="userInfo">
                <i className="icon-info" />
                {t('RELEASE_NOTES')}
              </NavLink>
              <UserMenuSettings />
              <ChangeLanguage />
              <span className="build-ver-container">{`build ${version.buildId}`}</span>
            </div>
          </div>
        </div>
      </div>
      {!mobileVersion && (
        <div className={sidebarExpanded ? 'toolbar vertical active' : 'toolbar vertical'}>
          <div className="toolArea">
            <a
              id="menu-action"
              className={sidebarClicked ? 'active' : ''}
              onClick={() => expandSidebar(!sidebarExpanded)}
            >
              <i className="icon-bars" />
            </a>
            <ul className="toolMenu">
              <li className={activeMenuItem(['/'], true)}>
                <NavLink to="/" exact={true} title={t('GENERAL_VIEW_MENU')}>
                  <i className="icon-home" />
                  <span>{t('GENERAL_VIEW_MENU')}</span>
                </NavLink>
              </li>
              <li className={activeMenuItem(['/generalContracts'])}>
                <NavLink to="/generalContracts" title={t('GENERAL_CONTRACTS_MENU')}>
                  <i className="icon-contract" />
                  <span>{t('GENERAL_CONTRACTS_MENU')}</span>
                </NavLink>
              </li>
              <li className={activeMenuItem(['/orderAndDelivery'])}>
                <NavLink to="/orderAndDelivery" title={t('DELIVERY_RETURN')}>
                  <i className="icon-garageInOut" />
                  <span>{t('DELIVERY_RETURN')}</span>
                </NavLink>
              </li>
              {!!user && PermissionManager.canViewCosts(user) && (
                <li className={activeMenuItem(['/deltaContracts'])}>
                  <NavLink
                    to={`/deltaContracts${
                      FrontendSettings.get(FrontendSettingsKeys.deltaContractsMode) === 'old' ? 'Old' : ''
                    }`}
                    title={t('DELTA_CONTRACTS_MENU')}
                  >
                    <i className="icon-delta" />
                    <span>{t('DELTA_CONTRACTS_MENU')}</span>
                  </NavLink>
                </li>
              )}
              {!!user && PermissionManager.canViewReports(user) && (
                <li className={activeMenuItem(['/reports'])}>
                  <NavLink to="/reports" title={t('REPORTS')}>
                    <i className="icon-report" />
                    <span>{t('REPORTS')}</span>
                  </NavLink>
                </li>
              )}

              {hasOrderForms && (
                <li className={activeMenuItem(['/requests'])}>
                  <NavLink to="/requests" title={t('REQUESTS')}>
                    <i className="icon-requests" />
                    <span>{t('REQUESTS')}</span>
                  </NavLink>
                </li>
              )}
              {user?.isInternalUser && (
                <li className={activeMenuItem(['/damages'])}>
                  <NavLink to="/damages" title={t('DAMAGES')}>
                    <i className="icon-car-damage" style={{ fontSize: '35px' }} />
                    <span>{t('DAMAGES')}</span>
                  </NavLink>
                </li>
              )}
              {isDevAndStaging && (
                <li className={activeMenuItem(['/translations'])}>
                  <NavLink to="/translations" title={t('TRANSLATIONS')}>
                    <i className="icon-edit" />
                    <span>{t('TRANSLATIONS')}</span>
                  </NavLink>
                </li>
              )}
              {isDevAndStaging && user?.isInternalUser && (
                <li className={activeMenuItem(['/dashboard'])}>
                  <NavLink to="/dashboard" title={t('DASHBOARD')}>
                    <i className="icon-statistics" />
                    <span>{t('DASHBOARD')}</span>
                  </NavLink>
                </li>
              )}
            </ul>
            <ul className="adminheaderArea">
              {user?.isInternalUser && (
                <li
                  className={activeMenuItem([
                    '/UserManagement',
                    '/assignCustomer',
                    '/tenant/',
                    '/customer/',
                    '/user/',
                    '/unit/',
                  ])}
                >
                  <NavLink to="/UserManagement" title={t('ADMIN_PANEL')}>
                    <i className="icon-users" />
                    <span>{t('ADMIN_PANEL')}</span>
                  </NavLink>
                </li>
              )}
              {user?.isAdmin && (
                <li className={activeMenuItem(['/RequestsManagement'])}>
                  <NavLink to="/RequestsManagement" title={t('REQUESTS_MANAGEMENT')}>
                    <i className="icon-requests-man" />
                    <span>{t('REQUESTS_MANAGEMENT')}</span>
                  </NavLink>
                </li>
              )}
              {user?.isAdmin && (
                <li className={activeMenuItem(['/ReportsManagement'])}>
                  <NavLink to="/ReportsManagement" title={t('REPORTS_MANAGEMENT')}>
                    <i className="icon-reports-man" />
                    <span>{t('REPORTS_MANAGEMENT')}</span>
                  </NavLink>
                </li>
              )}
              {user?.isAdmin && (
                <li className={activeMenuItem(['/NotificationsManagement'])}>
                  <NavLink to="/NotificationsManagement" title={t('NOTIFICATIONS_MANAGEMENT')}>
                    <i className="icon-notification" />
                    <span>{t('NOTIFICATIONS_MANAGEMENT')}</span>
                  </NavLink>
                </li>
              )}
            </ul>
          </div>
        </div>
      )}
    </>
  )
}
